import React from 'react';
import { useIntl } from 'react-intl';

import { ModalLayout } from '@/features/modal/modals/ModalLayout';
import { Button } from '@/components/buttons/Button';
import { TranslatedRichText } from '@/components/TranslatedRichText';
import { BaseModalProps } from '@/features/modal/types';
import { useLogin } from '@/hooks/useLogin';
import { GTagEvents, OpenedFrom, triggerGTag } from '@/lib/gTagManager';
import { getAlbertaRedirectUrl, redirectToExternalSite } from '@/utils/alberta-redirect';
import storageManager from '@/shared/storageManager';

export const AlbertaMigrationGateModal = ({ closeModal }: BaseModalProps): JSX.Element => {
  const intl = useIntl();
  const { openLogin } = useLogin();
  const isReturningUser = !!storageManager.getIsReturningUser();
  const redirectUrl = getAlbertaRedirectUrl('roc', false, isReturningUser);

  const onStay = () => {
    closeModal();
    openLogin({ openedFrom: OpenedFrom.AlbertaMigrationGate });
  };

  const onGo = (e: React.MouseEvent<HTMLAnchorElement>) => {
    triggerGTag(GTagEvents.alberta_migrate_cta);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return; // let the browser handle modifier/middle clicks natively
    }
    e.preventDefault();
    redirectToExternalSite(redirectUrl);
  };

  return (
    <ModalLayout data-testid="alberta-migration-gate-modal" close={closeModal}>
      <div className="w-full text-center grid grid-cols-1 gap-6">
        <h1 className="text-xl font-bold">
          {intl.formatMessage({ id: 'alberta.migration.gate.header' })}
        </h1>
        <TranslatedRichText id="alberta.migration.gate.text" tagName="div" />
        <a
          href={redirectUrl}
          onClick={onGo}
          className="relative w-full rounded-3xl p-3 bg-blue-light hover:bg-cyan-600 text-white text-md text-center font-bold leading-6 transition"
        >
          {intl.formatMessage({ id: 'alberta.migration.gate.cta.go' })}
        </a>
        <Button
          onClickHandler={onStay}
          btnType="outline"
          text={intl.formatMessage({ id: 'alberta.migration.gate.cta.stay' })}
        />
        <button
          type="button"
          className="text-sm underline text-gray-500 hover:text-gray-700"
          onClick={onStay}
        >
          {intl.formatMessage({ id: 'alberta.migration.gate.link.not.alberta' })}
        </button>
      </div>
    </ModalLayout>
  );
};
